/**
 * Unit 2.6 · IMPL: one conversation in the client's Polly list.
 *
 * Bead: insurescope-11zkc.3.6.3. Design contract:
 * docs/design-briefs/2026-06-03-client-conversations.md. The whole row is
 * ONE block-level Link (⌘-click must work); three-line stack: title +
 * agent chip, snippet (or the italic placeholder; italic is reserved
 * for the placeholder so the two states stay visually distinct), meta
 * with a pluralized message count and strict relative time.
 */
import { formatDistanceToNowStrict } from "date-fns";
import { MessageSquare, Users } from "lucide-react";
import { Link } from "react-router";

import { IChatConversationListItem } from "@/api";
import { AgentBadge } from "@/components/shared/AgentBadge";
import { TeamVisibleBadge } from "@/components/shared/TeamVisibleBadge";

import { pluralCount } from "../utils/pluralCount";

export interface IConversationRowProps {
    conversation: IChatConversationListItem;
}

export const ConversationRow = ({ conversation }: IConversationRowProps) => {
    const {
        id,
        title,
        agent_name,
        last_message_snippet,
        message_count,
        last_message_at,
        updated_at,
        access_mode,
        shared_by_display_name,
        is_team_visible,
    } = conversation;

    // The tab now lists conversations shared WITH the viewer alongside their
    // own; the chip keeps "owned vs shared" legible (mirrors the Polly sidebar
    // "Shared by {name}"). Owned rows render no chip. Falls back to a plain
    // "Shared" when the owner's name is unavailable.
    //
    // VS7 (R10): within shared rows, a team-visible conversation reads as the
    // shared "Team" badge instead of "Shared by {name}" — team-visibility wins
    // over the personal attribution, so a team thread never masquerades as a
    // 1:1 share. The two indicators never co-render; owned rows show neither.
    const isShared = access_mode === "collaborator";
    const sharedLabel = shared_by_display_name
        ? `Shared by ${shared_by_display_name}`
        : "Shared";

    return (
        <Link
            to={`/organizations/chat/${id}`}
            className="hover:bg-muted/50 focus-visible:ring-ring block px-4 py-3 transition-colors focus-visible:ring-2 focus-visible:outline-none"
        >
            <span className="flex items-center gap-2">
                <span className="truncate text-sm font-medium">{title}</span>
                {agent_name && <AgentBadge agent={agent_name} />}
                {isShared &&
                    (is_team_visible ? (
                        <TeamVisibleBadge className="shrink-0" />
                    ) : (
                        <span
                            className="bg-muted text-muted-foreground inline-flex max-w-48 shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
                            data-testid="shared-badge"
                            title={sharedLabel}
                        >
                            <Users className="size-3 shrink-0" aria-hidden />
                            <span className="truncate">{sharedLabel}</span>
                        </span>
                    ))}
            </span>
            {last_message_snippet ? (
                <p className="text-muted-foreground mt-0.5 line-clamp-1 text-sm">
                    {last_message_snippet}
                </p>
            ) : (
                <p className="text-muted-foreground/70 mt-0.5 text-sm italic">
                    No messages yet
                </p>
            )}
            <span className="text-muted-foreground mt-1 flex items-center gap-1.5 text-xs">
                <MessageSquare className="size-3.5" aria-hidden />
                <span className="tabular-nums">
                    {pluralCount(message_count, "message")}
                </span>
                <span aria-hidden>·</span>
                {/* A zero-message conversation still has a truthful age. */}
                <span>
                    {formatDistanceToNowStrict(
                        new Date(last_message_at ?? updated_at),
                        { addSuffix: true }
                    )}
                </span>
            </span>
        </Link>
    );
};
